import { CreateEntrepriseForm } from "../types/forms/create-entreprise-form";
import { UpdateEntrepriseForm } from "../types/forms/update-company-form";
import { CustomDataSettingForm } from "../types/forms/custom-data-setting-form";
import { Collaborateur } from "../types/models/collaborateur";
import { Driver } from "../types/models/driver";
import { Entreprise } from "../types/models/entreprise";
import { DemandeApprobation } from "../types/models/demande-approbation";
import { HttpClient } from "./http-client";

export class EntrepriseService {
  private readonly http: HttpClient;

  constructor(apiUrl: string) {
    this.http = new HttpClient(apiUrl);
  }

  async getEntrepriseById(idEntreprise: number): Promise<Entreprise> {
    return this.http.get<Entreprise>(`/api/entreprises/${idEntreprise}`);
  }

  async getEntreprisesClientes(): Promise<Entreprise[]> {
    return this.http.get<Entreprise[]>("/api/entreprises/client");
  }

  async getEntreprisesPartenaires(): Promise<Entreprise[]> {
    return this.http.get<Entreprise[]>("/api/entreprises/partenaire");
  }

  async createEntreprise(entreprise: CreateEntrepriseForm): Promise<void> {
    await this.http.post("/api/entreprises", entreprise);
  }

  async createEntrepriseAdmin(entreprise: CreateEntrepriseForm): Promise<void> {
    await this.http.post("/api/entreprises/admin", entreprise);
  }

  async updateEntreprise(
    idEntreprise: number,
    entreprise: CreateEntrepriseForm,
  ): Promise<void> {
    await this.http.put(`/api/entreprises/${idEntreprise}`, entreprise);
  }

  async createNewDemandApproval(idEntreprise: number): Promise<void> {
    await this.http.post(`/api/entreprises/${idEntreprise}/approbation`);
  }

  async approbationEntreprise(
    idEntreprise: number,
    idApprobation: number,
    message: any,
  ): Promise<void> {
    await this.http.patch(
      `/api/entreprises/${idEntreprise}/approbation/${idApprobation}`,
      message,
    );
  }

  async getCompanyWaitingApproval(): Promise<DemandeApprobation[]> {
    return this.http.get<DemandeApprobation[]>(
      "/api/entreprises/demande-approbation",
    );
  }

  async updateEntrepriseAdmin(
    idEntreprise: number,
    entreprise: UpdateEntrepriseForm,
  ): Promise<void> {
    await this.http.put(`/api/entreprises/${idEntreprise}/admin`, entreprise);
  }

  async getContactsFromCompany(idEntreprise: number): Promise<Collaborateur[]> {
    return this.http.get<Collaborateur[]>(
      `/api/entreprises/${idEntreprise}/passagers`,
    );
  }

  /**
   * Donne le rôle de gestionnaire à un contact déjà rattaché à l'entreprise.
   * Ses autres rôles survivent, et l'appel est sans effet s'il l'est déjà.
   */
  async addAdministrateur(
    idEntreprise: number,
    idContact: number,
  ): Promise<void> {
    await this.http.post(
      `/api/entreprises/${idEntreprise}/administrateurs/${idContact}`,
    );
  }

  /**
   * Retire une personne de l'entreprise : ses rôles tombent et son entrée au
   * carnet est désactivée. Pour ne retirer qu'un rôle, voir les méthodes
   * dédiées à chacun.
   */
  async removeMembre(idEntreprise: number, idContact: number): Promise<void> {
    await this.http.delete(
      `/api/entreprises/${idEntreprise}/membres/${idContact}`,
    );
  }

  async removeAdministrateur(
    idEntreprise: number,
    idContact: number,
  ): Promise<void> {
    await this.http.delete(
      `/api/entreprises/${idEntreprise}/administrateurs/${idContact}`,
    );
  }

  async getCollaborateursFromCompany(
    idEntreprise: number,
  ): Promise<Collaborateur[]> {
    return this.http.get<Collaborateur[]>(
      `/api/collaborateurs/entreprise/${idEntreprise}`,
    );
  }

  /**
   * Donne le rôle de collaborateur à un contact déjà rattaché à l'entreprise,
   * pendant de `createDriver` côté partenaire. Sur quelqu'un à qui on avait
   * retiré le rôle, ses conditions commerciales sont reprises telles quelles.
   */
  async addCollaborateur(
    idEntreprise: number,
    idContact: number,
  ): Promise<void> {
    await this.http.post(`/api/entreprises/${idEntreprise}/collaborateurs`, {
      idContact,
    });
  }

  /**
   * Retire le rôle de collaborateur. La personne reste au carnet de contacts de
   * l'entreprise, sans rôle : pour l'en détacher, c'est `removeMembre`.
   */
  async deleteCollaborateur(
    idEntreprise: number,
    idCollaborateur: number,
  ): Promise<void> {
    await this.http.delete(
      `/api/entreprises/${idEntreprise}/collaborateurs/${idCollaborateur}`,
    );
  }

  async addCustomDataSetting(
    idEntreprise: number,
    customDataSetting: CustomDataSettingForm,
  ): Promise<void> {
    await this.http.post(
      `/api/entreprises/${idEntreprise}/custom-data-setting`,
      customDataSetting,
    );
  }

  async updateCustomDataSetting(
    idEntreprise: number,
    idCustomDataSetting: number,
    customDataSetting: CustomDataSettingForm,
  ): Promise<void> {
    await this.http.put(
      `/api/entreprises/${idEntreprise}/custom-data-setting/${idCustomDataSetting}`,
      customDataSetting,
    );
  }

  async deleteCustomDataSetting(
    idEntreprise: number,
    idCustomDataSetting: number,
  ): Promise<void> {
    await this.http.delete(
      `/api/entreprises/${idEntreprise}/custom-data-setting/${idCustomDataSetting}`,
    );
  }

  async getDriversFromCompany(idEntreprise: number): Promise<Driver[]> {
    return this.http.get<Driver[]>(
      `/api/entreprises/${idEntreprise}/chauffeur`,
    );
  }
}
